import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { supabase } from '@/lib/supabase';
import { useToast } from '@/shared/hooks/use-toast';

export const getTeamNoteQueryKey = (projectId: number) => [
  'team-notes',
  projectId,
];

async function getTeamNote({
  projectId,
}: {
  projectId: number;
}): Promise<string> {
  const { data, error } = await supabase
    .from('project_notes')
    .select('content')
    .eq('project_id', projectId)
    .eq('note_type', 'team')
    .maybeSingle();
  if (error) throw error;
  return data?.content ?? '';
}

async function upsertTeamNote({
  projectId,
  content,
}: {
  projectId: number;
  content: string;
}): Promise<void> {
  const { error } = await supabase.rpc('upsert_team_note', {
    p_project_id: projectId,
    p_content: content,
  });

  if (error) throw error;
}

export const useTeamNotes = (projectId: number) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });

  const query = useQuery({
    queryKey: getTeamNoteQueryKey(projectId),
    queryFn: () => getTeamNote({ projectId }),
  });

  const mutation = useMutation({
    mutationFn: ({ content }: { content: string }) =>
      upsertTeamNote({ projectId, content }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getTeamNoteQueryKey(projectId),
      });
      toast({ title: t('team_note_saved') });
    },
    onError: () => {
      toast({
        title: t('team_note_error'),
        variant: 'destructive',
      });
    },
  });

  return {
    data: query.data ?? '',
    isLoading: query.isLoading,
    mutation,
  };
};
